/**
 * @file src/utils/terms-loader.js
 * @description Locale-aware loader for terms and conditions HTML documents.
 * Each locale has a standalone HTML file in src/assets/terms/.
 * Falls back: full locale → base language → English.
 */

import { sanitizeTermsHtml } from '@/utils/security'

/**
 * Static import map for terms HTML files.
 * Add new locales here as they become available.
 * @type {Record<string, () => Promise<{default: string}>>}
 */
const termsImports = {
  en: () => import('@/assets/terms/en.html'),
  'en-US': () => import('@/assets/terms/en-US.html'),
  fr: () => import('@/assets/terms/fr.html'),
  de: () => import('@/assets/terms/de.html'),
  es: () => import('@/assets/terms/es.html'),
  it: () => import('@/assets/terms/it.html'),
  el: () => import('@/assets/terms/el.html'),
  nl: () => import('@/assets/terms/nl.html'),
  pt: () => import('@/assets/terms/pt.html'),
  da: () => import('@/assets/terms/da.html'),
  nb: () => import('@/assets/terms/nb.html'),
  sv: () => import('@/assets/terms/sv.html'),
  is: () => import('@/assets/terms/is.html'),
  cs: () => import('@/assets/terms/cs.html'),
  ja: () => import('@/assets/terms/ja.html'),
  'zh-CN': () => import('@/assets/terms/zh-CN.html'),
  'zh-HK': () => import('@/assets/terms/zh-HK.html'),
}

/**
 * Loads and sanitizes the terms HTML for a given locale.
 * Falls back from full locale (e.g. 'en-US') to base language ('en') to English.
 * @param {string} locale - The locale code (e.g. 'en-US', 'fr-FR', 'de')
 * @returns {Promise<string>} The sanitized terms HTML string
 */
export async function loadTermsHtml(locale) {
  const base = locale.split('-')[0]
  const loader = termsImports[locale] || termsImports[base] || termsImports.en

  const module = await loader()
  return sanitizeTermsHtml(module.default || module)
}
